"use client";

import { useEffect, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  prefix: string;
  value: number;
  suffix: string;
  display: string;
  label: string;
  description: string;
};
const stats: Stat[] = [{
  prefix: "",
  value: 40,
  suffix: "+",
  display: "40+",
  label: "YEARS EXPERIENCE",
  description: "Decades of master craftsmanship in the Collegeville region."
}, {
  prefix: "$",
  value: 150,
  suffix: "M",
  display: "$150M",
  label: "PROJECT VALUE",
  description: "Total value of residential and commercial developments delivered."
}, {
  prefix: "",
  value: 850,
  suffix: "+",
  display: "850+",
  label: "COMPLETED PROJECTS",
  description: "Custom homes, renovations, and property management success stories."
}, {
  prefix: "",
  value: 100,
  suffix: "%",
  display: "100%",
  label: "CLIENT RETENTION",
  description: "Building lasting relationships through integrity and precision."
}];
function AnimatedCounter({
  stat,
  start
}: {
  stat: Stat;
  start: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1800;
    const startTime = performance.now();
    let frame: number;
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * stat.value));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setCount(stat.value);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, stat.value]);
  return <span className="tabular-nums">
      {stat.prefix}
      {count}
      {stat.suffix}
    </span>;
}
export default function Standard() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="741" id="the-moscariello-standard-in-numbers" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 dark">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs sm:text-sm tracking-widest text-primary mb-6">PERFORMANCE METRICS</p>
            <h2 className="font-default text-4xl sm:text-5xl md:text-[48px] font-semibold tracking-tight leading-[1.05]">
              The Moscariello Standard.
            </h2>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-y-12 lg:gap-y-0 relative">
          {stats.map((stat, i) => <div data-index={i} key={stat.label} className={`relative px-4 sm:px-6 lg:px-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: `${i * 120}ms`
        }}>
              {}
              {i > 0 && <Separator orientation="vertical" className="hidden lg:block absolute left-0 top-2 bottom-2 bg-border/60" />}
              {}
              {i % 2 === 1 && <Separator orientation="vertical" className="lg:hidden absolute left-0 top-2 bottom-2 bg-border/40" />}
              {}
              {i >= 2 && <Separator orientation="horizontal" className="lg:hidden absolute -top-6 left-2 right-2 bg-border/40" />}

              {}
              <div className="relative">
                <div aria-hidden="true" className={`absolute inset-0 -m-4 rounded-lg blur-3xl transition-opacity duration-1000 ${isVisible ? "opacity-30" : "opacity-0"}`} style={{
              background: "radial-gradient(circle at center, var(--primary) 0%, transparent 70%)"
            }} />
                <div className="relative font-mono font-semibold text-primary text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-none tracking-tight">
                  <AnimatedCounter stat={stat} start={isVisible} />
                </div>
              </div>

              {}
              <div className="mt-5 font-mono text-[11px] sm:text-[13px] tracking-[0.15em] text-muted-foreground uppercase">
                {stat.label}
              </div>

              {}
              <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-foreground/80 max-w-[240px]">
                {stat.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}